// FIÓK-INTEGRÁCIÓK — a fiók külső rendszer-azonosítói (HotelCMS · ZadirCMS · MailWizz), rendszerenként TÖBB is.
//
// MIÉRT LISTA: az Atlasban a fiók egy hotel, a HotelCMS-ben viszont az egység egy DOMAIN — egy hotelnek lehet külön
// loyalty-, booking- és ticket-shop-domainje, mindegyik saját azonosítóval. A fiók korábban rendszerenként EGY
// azonosítót tudott (`hotelcmsId` · `zadircmsId` · `mailwizzId`), így egy második domain felvétele az elsőt
// kiszorította, és a róla mért használat kiesett a számláról. Ezért a fiók-rekord `integrations` listát hordoz
// (rendszer · azonosító · megjegyzés), és a szolgáltatás-hozzárendelés a lista elemeire HIVATKOZIK (`usageRefs`).
//
// EGYEDISÉG SZÁNDÉKOSAN NINCS (user-döntés): ugyanaz a külső azonosító több fiókon vagy kétszer is állhat — a
// dupla felvétel felhasználói hiba, nem a rendszer fala.
//
// A RÉGI HÁROM MEZŐ az átállás alatt még él: ahol a fiók-rekordon nincs `integrations` (az új mezőt még semmi nem
// írta rá — régi sor, vagy az ETL hozta létre), ott a lista a régi mezőkből ÁLL ELŐ, és az elemek azonosítója
// DETERMINISZTIKUS (fiók + rendszer), hogy a hozzárendelés kötése, a fiók-lap és a bekötő szkript ugyanazt lássa.
// Az írás fordítva tükröz: a lista rendszerenkénti ELSŐ eleme a régi mezőbe is bekerül (visszaesés).
/** A támogatott külső rendszerek (a `clients/account` séma `integrations[].system` enumja). */
export const INTEGRATION_SYSTEMS = ['hotelcms', 'zadircms', 'mailwizz'];
/** A rendszer RÉGI, egy-azonosítós fiók-mezője (az átállás alatti visszaesés és tükör). */
export const LEGACY_INTEGRATION_FIELD = {
    hotelcms: 'hotelcmsId',
    zadircms: 'zadircmsId',
    mailwizz: 'mailwizzId',
};
/** A külső azonosító és a megjegyzés plafonja (a séma `maxLength`-jei). */
export const INTEGRATION_EXTERNAL_ID_MAX = 128;
export const INTEGRATION_NOTE_MAX = 500;
/** Egy fiókon legfeljebb ennyi azonosító állhat (a séma `maxItems`-e). */
export const INTEGRATIONS_MAX = 50;
export function isIntegrationSystem(value) {
    return typeof value === 'string' && INTEGRATION_SYSTEMS.includes(value);
}
/** 32 bites FNV-1a — kicsi, függőség nélküli, mindkét runtime-on (api + web) azonos eredményt ad. */
function fnv1a(input, seed) {
    // az FNV offset basis a seed-del eltolva: négy független 32 bites szelet egy 128 bites azonosítóhoz
    let hash = (0x811c9dc5 ^ seed) >>> 0;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
}
/**
 * A régi mezőből előállított integráció DETERMINISZTIKUS azonosítója — RFC 9562 v8 („egyedi") UUID a fiók és a
 * rendszer hash-éből. Ugyanarra a (fiók, rendszer) párra mindig ugyanazt adja, így a régi mezőből olvasott lista
 * eleme olvasásról olvasásra stabil, és a bekötő szkript ugyanazt az azonosítót írja be, amit a lap mutatott.
 * Ütközés csak egy fiókon belül számítana (a kötés `(accountId, integrationId)` pár), ott pedig rendszerenként
 * egy ilyen elem van.
 */
export function legacyIntegrationId(accountId, system) {
    const key = `${accountId}|${system}`;
    const hex = [1, 2, 3, 4].map((seed) => fnv1a(key, seed)).join('');
    // verzió-nibble = 8, variáns-bitek = 10xx (RFC 9562)
    const variant = ((parseInt(hex.charAt(16), 16) & 0x3) | 0x8).toString(16);
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        `8${hex.slice(13, 16)}`,
        `${variant}${hex.slice(17, 20)}`,
        hex.slice(20, 32),
    ].join('-');
}
function trimmedOrNull(value) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}
/** Igaz, ha a fiók-rekordon MÁR áll `integrations` lista (akár üres) — azaz a régi mezők már nem számítanak. */
export function hasIntegrationList(data) {
    return Array.isArray(data.integrations);
}
/**
 * A fiók integrációi a nyers fiók-adatból. Ha a rekordon áll `integrations` lista, az a forrás-igazság (az
 * érvénytelen elemek kimaradnak — a séma ezt amúgy sem engedné be); ha nincs, a régi három mezőből áll elő,
 * determinisztikus azonosítóval (l. `legacyIntegrationId`).
 */
export function readAccountIntegrations(accountId, data) {
    if (Array.isArray(data.integrations)) {
        const out = [];
        for (const raw of data.integrations) {
            if (raw === null || typeof raw !== 'object')
                continue;
            const item = raw;
            const externalId = trimmedOrNull(item.externalId);
            if (typeof item.id !== 'string' || !isIntegrationSystem(item.system) || !externalId)
                continue;
            out.push({ id: item.id, system: item.system, externalId, note: trimmedOrNull(item.note) });
        }
        return out;
    }
    const out = [];
    for (const system of INTEGRATION_SYSTEMS) {
        const externalId = trimmedOrNull(data[LEGACY_INTEGRATION_FIELD[system]]);
        if (externalId) {
            out.push({ id: legacyIntegrationId(accountId, system), system, externalId, note: null });
        }
    }
    return out;
}
/**
 * A lista TÜKRE a régi három mezőre: rendszerenként az ELSŐ elem külső azonosítója, vagy null. Az írás ezzel
 * tartja a régi mezőket összhangban (visszaesés egy korábbi verzióra, és a régi mezőt olvasó külső eszközök).
 */
export function legacyIntegrationFields(integrations) {
    const out = {
        hotelcmsId: null,
        zadircmsId: null,
        mailwizzId: null,
    };
    for (const system of INTEGRATION_SYSTEMS) {
        const first = integrations.find((i) => i.system === system);
        out[LEGACY_INTEGRATION_FIELD[system]] = first ? first.externalId : null;
    }
    return out;
}
/**
 * A tárolt kötés beolvasása a nyers hozzárendelés-sorból (`usageRefs` oszlop). Hiányzó / nem-tömb → `null` =
 * a sor MÉG NINCS kötve (a bevezetés előtti állapot — a fogyasztók a régi szabállyal számolnak); az érvénytelen
 * elemek kimaradnak. Közös, mert az írás-validáció, a generátor, az előrejelzés és a használati lap is ezt olvassa.
 */
export function readUsageRefs(raw) {
    if (!Array.isArray(raw))
        return null;
    const out = [];
    for (const item of raw) {
        if (item === null || typeof item !== 'object')
            continue;
        const { accountId, integrationId } = item;
        if (typeof accountId === 'string' && typeof integrationId === 'string') {
            out.push({ accountId, integrationId });
        }
    }
    return out;
}
/** Egy hozzárendelés legfeljebb ennyi azonosítóra köthet (a séma `maxItems`-e). */
export const USAGE_REFS_MAX = 200;
/**
 * A JUTALÉKOS használat-források — EGY helyen (a szerződés `COMMISSION_USAGE_SOURCE_TYPES`-a ezt adja tovább): a
 * jutalék-lekérés ezekre ír sort, és ezeket CSAK a HotelCMS méri (a jutalék-riport kulcsa a HotelCMS
 * `domains_v2.id`-ja; ZadirCMS-tartalék itt nincs).
 */
export const COMMISSION_USAGE_SOURCE_TYPES = [
    'rs_direct',
    'rs_referral',
    'rs_direct_referral',
    'rs_voucher',
    'ticket',
];
/**
 * A használat-forrás (`usageSourceType`) MELY rendszer azonosítóját méri: a hírlevél-darabszám a MailWizzből
 * jön, a jutalék CSAK a HotelCMS-ből, a többi loyalty-forrás (loyalty-hírlevél, loyalty-regisztráltak) a
 * HotelCMS-ből — vagy a ZadirCMS-ből annál a néhány fióknál, amelyik ott fut. A kötés csak ilyen rendszerű
 * azonosítóra mutathat, és a régi szabály (kötés nélküli sor) is ebben a sorrendben választ.
 */
export function usageSourceSystems(usageSourceType) {
    if (usageSourceType === 'newsletter')
        return ['mailwizz'];
    if (COMMISSION_USAGE_SOURCE_TYPES.includes(usageSourceType)) {
        return ['hotelcms'];
    }
    return ['hotelcms', 'zadircms'];
}
/**
 * A MEGOSZTOTT HASZNÁLAT (`splitSharedUsage`) mely forrásnál kapcsolható: CSAK a regisztráltak utáni loyalty-díjnál
 * (`rs_loyalty`). Egy közös loyalty-programon több hotel osztozhat, és van ügyfél, akinél a regisztráltak utáni díj
 * a hotelek között OSZLIK (user-döntés). A loyalty-HÍRLEVÉL (`loyalty_newsletter`) és minden más forrás nem osztható.
 */
export const SPLIT_SHARED_USAGE_SOURCES = ['rs_loyalty'];
/** Kapcsolható-e a megosztott használat ennél a forrásnál (a BFF fala és az űrlap ugyanezt kérdezi). */
export function canSplitSharedUsage(usageSourceType) {
    return (typeof usageSourceType === 'string' && SPLIT_SHARED_USAGE_SOURCES.includes(usageSourceType));
}
/**
 * Egy fiók azonosítói közül a forráshoz KÖTHETŐK: a rendszer illik a forráshoz (`usageSourceSystems`).
 *
 * _(Korábban a ZadirCMS-azonosító CSAK HotelCMS nélküli fiókon volt köthető, mert a lekérés a fiók két számát EGY
 * sorba vonta össze a HotelCMS-kulcson. Mióta a használati sor kulcsa a rendszert is tartalmazza (`usageRefKey`),
 * a két rendszer száma két külön sor — egy két rendszerben futó hotelnél a hozzárendelés kötése mondja meg, melyik
 * szám számít, vagy mindkettő.)_
 */
export function bindableIntegrations(integrations, usageSourceType) {
    const systems = usageSourceSystems(usageSourceType);
    return integrations.filter((i) => systems.includes(i.system));
}
/**
 * A HASZNÁLATI SOR KULCSA egy külső azonosítóra: `rendszer:azonosító`. A rendszerek azonosító-terei FÜGGETLENEK —
 * élesben a HotelCMS 512 a Kolping, a ZadirCMS 512 az Echo Residence —, ezért a puszta szám nem kulcs. A
 * feloldó (a hozzárendelés kötéséből), a mennyiség-, állapot- és deviza-térképek és a használati lap is ezt a
 * kulcsot használja.
 */
export function usageRefKey(system, externalId) {
    return `${system}:${externalId}`;
}
const USAGE_SYSTEMS = ['mailwizz', 'hotelcms', 'zadircms'];
/**
 * Egy tárolt használati sor RENDSZERE. A sor `sourceSystem` mezője az igazság; hiányában (régi író) a típusból és
 * a `raw_data`-ból vezetjük le — ugyanazzal a szabállyal, amivel az Odin-migráció a meglévő sorokat kitöltötte:
 * hírlevél → MailWizz · jutalék → HotelCMS · loyalty → ZadirCMS, ha a sor CSAK ZadirCMS-azonosítót hordoz és a kulcs
 * az, különben HotelCMS.
 */
export function usageRowSystem(usageSourceType, sourceSystem, sourceReferenceId, rawData) {
    if (typeof sourceSystem === 'string' &&
        USAGE_SYSTEMS.includes(sourceSystem)) {
        return sourceSystem;
    }
    if (usageSourceType === 'newsletter')
        return 'mailwizz';
    if (COMMISSION_USAGE_SOURCE_TYPES.includes(usageSourceType))
        return 'hotelcms';
    const raw = rawData !== null && typeof rawData === 'object' ? rawData : {};
    // a `raw_data`-azonosító lehet szám vagy szöveg (a legacy és az Atlas lekérése is ír)
    const idOf = (v) => typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';
    if (idOf(raw.hotelcms_id) === '' && idOf(raw.zadircms_id) === sourceReferenceId)
        return 'zadircms';
    return 'hotelcms';
}
//# sourceMappingURL=account-integrations.js.map